import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DesktopLayout from '../../../layout/DesktopLayout';
import BottomNav from '../../../BottomNav';
import { useDesktopTopBar } from '../../../../context/DesktopLayoutContext';
import { useBranch } from '../../../../context/BranchContext';
import { useDisplayStore, useDisplayToastStore } from '../../../../redux/display/display/displayHooks';
import { getDisplayMediaCandidateUrls } from '../../../../services/displayApi';
import { displayPaths } from '../../paths';
import type { DisplayItem, SpotLightMedia } from '../../data/types';
import { T, useIsDesktop } from '../../../reports/reportTokens';
import { MediaCarouselPreviewModal } from '../../components/ui/MediaCarouselPreviewModal/MediaCarouselPreviewModal';
import { DisplayToastContainer } from '../../components/ui/DisplayToastContainer/DisplayToastContainer';
import { OrientationIcon } from '../../components/ui/OrientationIcon/OrientationIcon';
import './DisplayListPage.css';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FormattedSlot {
  days: string;
  time: string;
}

interface DisplayRow {
  displayItem: DisplayItem;
  seqNum: number;
  displayName: string;
  type: 'Single' | 'Playlist';
  orientation: 'Landscape' | 'Portrait';
  mediaList: SpotLightMedia[];
  slots: FormattedSlot[];
}

const isPortraitOrientation = (orientation?: string | null) => (orientation ?? '').toLowerCase() === 'portrait';

function formatTime12h(timeStr?: string): string {
  if (!timeStr) return '';
  if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
  const parts = timeStr.split(':');
  if (parts.length < 2) return timeStr;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1];
  if (isNaN(hours)) return timeStr;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${ampm}`;
}

const ALL_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function getFormattedScheduleSlots(item: DisplayItem): FormattedSlot[] {
  // attribute.timeSlots is the authoritative field — it's exactly what Create/Edit Display sends and
  // what the backend echoes back. Fall back to a media entry's showTime only for older data shapes
  // that never carried a top-level timeSlots array.
  let slots = item.attribute?.timeSlots;
  if (!slots || slots.length === 0) {
    const spotLightMedia = item.attribute?.spotLightMedia ?? [];
    const firstWithSlots = spotLightMedia.find((m) => Array.isArray(m.showTime) && m.showTime.length > 0);
    slots = firstWithSlots?.showTime;
  }
  if (!slots || slots.length === 0) {
    return [{ days: 'Every day', time: 'All Day' }];
  }

  // Group by time range first, so a slot covering all 7 weekdays collapses into one "Every day" pill
  // instead of 7 separate day pills — mirrors MediaScheduler's own day-grouping on the create/edit side.
  const groups = new Map<string, Set<string>>();
  (slots as any[]).forEach((slot) => {
    const startTime = slot.start_time || slot.startTime;
    const endTime = slot.end_time || slot.endTime;
    let daysStr = slot.weekday || (Array.isArray(slot.days) && slot.days.length > 0 ? slot.days.join(', ') : 'Every day');
    if (daysStr.toLowerCase() === 'everyday') daysStr = 'Every day';
    const key = `${startTime ?? ''}__${endTime ?? ''}`;
    const existing = groups.get(key);
    if (existing) existing.add(daysStr);
    else groups.set(key, new Set([daysStr]));
  });

  const result: FormattedSlot[] = [];
  for (const [key, daySet] of groups) {
    const [startTime, endTime] = key.split('__');
    const time = startTime && endTime ? `${formatTime12h(startTime)} – ${formatTime12h(endTime)}` : 'All Day';
    const isEveryDay = daySet.size === ALL_WEEKDAYS.length && ALL_WEEKDAYS.every((d) => daySet.has(d));
    if (isEveryDay) {
      result.push({ days: 'Every day', time });
    } else {
      daySet.forEach((days) => result.push({ days, time }));
    }
  }
  return result;
}

function SlotPill({ slot }: { slot: FormattedSlot }) {
  return (
    <div className="display-slot-pill">
      <Icon name="calendar_today" size={13} color="#64748B" />
      <span>{slot.days}</span>
      <span style={{ color: '#CBD5E1', margin: '0 2px', fontWeight: 400 }}>|</span>
      <span style={{ color: '#475569' }}>{slot.time}</span>
    </div>
  );
}

// ─── Columns: SEQ | TYPE | MEDIA | DISPLAY NAME | SLOTS | EDIT | DELETE ──────
const HEADERS = [
  { key: 'seq',         label: 'SEQ',          width: '50px' },
  { key: 'type',        label: 'TYPE',         width: '100px' },
  { key: 'media',       label: 'MEDIA',        width: '160px' },
  { key: 'name',        label: 'DISPLAY NAME', width: '300px' },
  { key: 'orientation', label: 'ORIENTATION',  width: '130px' },
  { key: 'slots',       label: 'SLOTS',        width: 'minmax(280px, 1fr)' },
  { key: 'actions',     label: 'ACTIONS',      width: '90px' },
];
const GRID = HEADERS.map(h => h.width).join(' ');
const TABLET_TABLE_MIN_WIDTH = '920px';
const TABLET_TABLE_BREAKPOINT = '(max-width: 1180px)';

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' ? window.matchMedia(query).matches : false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
}

const Icon = ({ name, size = 18, color = T.t3, style = {} }: any) => (
  <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, color, userSelect: 'none', ...style }}>{name}</span>
);

function Bone({ w, h, r = 4 }: { w: number | string; h: number; r?: number }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r, flexShrink: 0,
      background: `linear-gradient(90deg, ${T.cardBorder} 0%, #F0F2F7 50%, ${T.cardBorder} 100%)`,
      backgroundSize: '200% 100%',
      animation: 'bone-shimmer 1.4s ease-in-out infinite',
    }} />
  );
}

function ThumbLoadingRing() {
  return (
    <>
      <style>{`
        @keyframes display-thumb-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(17,24,39,0.55)' }}>
        <div style={{ width: 20, height: 20, border: '2.5px solid rgba(255,255,255,0.18)', borderTopColor: '#fff', borderRadius: '50%', animation: 'display-thumb-spin 0.7s linear infinite', flexShrink: 0 }} />
      </div>
    </>
  );
}

function MediaPlaceholder({
  isVideo,
  name,
  compact,
}: {
  isVideo: boolean;
  name?: string | null;
  compact?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: compact ? 4 : 8, padding: compact ? 4 : 12, textAlign: 'center' }}>
      <div style={{ width: compact ? 28 : 44, height: compact ? 28 : 44, borderRadius: compact ? 8 : 12, background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={isVideo ? 'videocam' : 'image'} size={compact ? 16 : 24} color={T.t3} />
      </div>
      {!compact && (
        <span style={{ fontSize: 11, fontWeight: 600, color: '#E5E7EB', fontFamily: T.font }}>
          {name || (isVideo ? 'Video' : 'Image')}
        </span>
      )}
    </div>
  );
}

// ─── Hero Banner ───────────────────────────────────────────────────────────────
function HeroBanner({ onAdd, mobile }: { onAdd: () => void; mobile?: boolean }) {
  return (
    <div className={`display-hero-banner ${mobile ? 'is-mobile' : ''}`}>
      <div className="display-hero-bg-circle" />
      <div className="display-hero-left">
        <div className="display-hero-icon-box">
          <Icon name="tv" size={26} color="#fff" />
        </div>
        <div>
          <div className="display-hero-title">Display</div>
          <div className="display-hero-subtitle">Manage display media and sequence order.</div>
        </div>
      </div>
      {!mobile && (
        <button onClick={onAdd} className="display-hero-add-btn">
          <Icon name="add" size={20} color="#fff" />
          Add New
        </button>
      )}
    </div>
  );
}

function DesktopShimmer() {
  return (
    <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 24, overflowX: 'hidden' }}>
      <HeroBanner onAdd={() => {}} />
      <div style={{ position: 'relative' }}>
        <Icon name="search" size={18} color={T.t3} style={{ position: 'absolute', left: 16, top: 13 }} />
        <input type="text" disabled placeholder="Search by display name..."
          style={{ width: '100%', border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '12px 18px 12px 44px', fontSize: 13, fontFamily: T.font, color: T.t1, outline: 'none', background: T.surface, boxSizing: 'border-box' }} />
      </div>
      <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, boxShadow: T.cardShadow, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: GRID, width: '100%', gap: 8, padding: '10px 14px', background: T.bg, borderLeft: '3px solid transparent', boxSizing: 'border-box' }}>
          {HEADERS.map(h => <div key={h.key} style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: T.font }}>{h.label}</div>)}
        </div>
        {[...Array(7)].map((_, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: GRID, width: '100%', gap: 8, alignItems: 'center', padding: '12px 14px', borderBottom: i < 6 ? `1px solid ${T.divider}` : 'none', borderLeft: '3px solid transparent', boxSizing: 'border-box' }}>
            <Bone w={24} h={12} r={4} />
            <Bone w={70} h={20} r={10} />
            <Bone w={160} h={85} r={12} />
            <Bone w="60%" h={13} r={4} />
            <Bone w="50%" h={12} r={4} />
            <Bone w={48} h={24} r={6} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileShimmer() {
  return (
    <div style={{ padding: '12px 16px 88px', display: 'flex', flexDirection: 'column', gap: 10, width: '100%', boxSizing: 'border-box' }}>
      <HeroBanner onAdd={() => {}} mobile />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Icon name="search" size={16} color={T.t3} style={{ position: 'absolute', left: 12, top: 10 }} />
          <input type="text" disabled placeholder="Search displays..."
            style={{ width: '100%', border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '9px 12px 9px 34px', fontSize: 12, fontFamily: T.font, color: T.t1, outline: 'none', background: T.surface, boxSizing: 'border-box' }} />
        </div>
        <button disabled style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '9px 14px', borderRadius: 12, border: 'none', background: T.accent, color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: T.font, flexShrink: 0 }}>
          <Icon name="add" size={16} color="#fff" /> Add
        </button>
      </div>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '12px 14px', boxShadow: T.shadow }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Bone w={100} h={65} r={8} />
            <div style={{ flex: 1 }}>
              <Bone w="55%" h={14} r={4} />
              <div style={{ height: 6 }} />
              <Bone w="35%" h={10} r={3} />
            </div>
            <Bone w={22} h={22} r={4} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DisplayEmptyState({ search, onAdd, mobile }: { search: string; onAdd: () => void; mobile?: boolean }) {
  const hasSearch = Boolean(search.trim());
  const title = hasSearch ? 'No displays found' : 'No display media yet';
  const subtitle = hasSearch ? 'Try adjusting your search or filters' : 'Add your first display media to get started';

  return (
    <div style={{ textAlign: 'center', padding: mobile ? '48px 24px' : 56, color: T.t3 }}>
      <Icon name={hasSearch ? 'search_off' : 'tv'} size={48} color={T.t3} style={{ display: 'block', margin: '0 auto 12px' }} />
      <div style={{ fontSize: mobile ? 15 : 16, fontWeight: 700, color: T.t1, marginBottom: 4, fontFamily: T.font }}>{title}</div>
      <div style={{ fontSize: mobile ? 12 : 13, color: T.t3, marginBottom: 16, fontFamily: T.font }}>{subtitle}</div>
      <button
        onClick={onAdd}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto',
          padding: mobile ? '9px 18px' : '10px 20px', borderRadius: 10, border: 'none',
          background: T.accent, color: '#fff', fontSize: mobile ? 12 : 13, fontWeight: 600,
          cursor: 'pointer', fontFamily: T.font,
        }}
      >
        <Icon name="add" size={16} color="#fff" />
        Add Display
      </button>
    </div>
  );
}

function MediaThumb({
  mediaId,
  mimeType,
  name,
  url,
  orientation,
  onClick,
  onSettled,
  width = 120,
  height = 78,
  bare = false,
}: {
  mediaId: string;
  mimeType: string;
  name: string;
  url?: string;
  orientation?: string | null;
  onClick?: () => void;
  onSettled?: () => void;
  width?: number;
  height?: number;
  bare?: boolean;
}) {
  const isVideo = mimeType?.startsWith('video/');
  const { mediaUrl, failed, handleMediaError } = useDisplayMediaPreviewSource({ mediaId, mimeType, name, url });
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const settledRef = useRef(false);
  const isPortrait = isPortraitOrientation(orientation);
  const shellWidth = bare ? width : (isPortrait ? 95 : width + 24);
  const shellHeight = bare ? height : (isPortrait ? 130 : height + 16);

  useEffect(() => {
    setLoaded(false);
    settledRef.current = false;
  }, [mediaUrl]);

  useEffect(() => {
    if (!isVideo && imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [mediaUrl, isVideo]);

  useEffect(() => {
    if (settledRef.current) return;
    if (loaded || failed || !mediaUrl) {
      settledRef.current = true;
      onSettled?.();
    }
  }, [loaded, failed, mediaUrl, onSettled]);

  const innerContent = (
    <div style={{
      width: bare ? '100%' : shellWidth - 14,
      height: bare ? '100%' : shellHeight - 14,
      background: 'linear-gradient(180deg, #172033 0%, #101827 100%)',
      border: bare ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', position: 'relative',
      borderRadius: bare ? 10 : (isPortrait ? 20 : 18),
    }}>
      {mediaUrl && !failed ? (
        isVideo ? (
          <video
            src={mediaUrl}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#111827', opacity: loaded ? 1 : 0 }}
            muted preload="metadata"
            onLoadedData={() => setLoaded(true)}
            onError={handleMediaError}
          />
        ) : (
          <img
            ref={imgRef}
            src={mediaUrl}
            alt={name || 'Display media'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#111827', opacity: loaded ? 1 : 0 }}
            onLoad={() => setLoaded(true)}
            onError={handleMediaError}
          />
        )
      ) : (
        <MediaPlaceholder isVideo={isVideo} name={name} compact />
      )}
      {mediaUrl && !failed && !loaded && <ThumbLoadingRing />}
    </div>
  );

  if (bare) return innerContent;

  return (
    <div
      onClick={onClick}
      style={{
        width: shellWidth, height: shellHeight, padding: 7, boxSizing: 'border-box', flexShrink: 0,
        background: 'linear-gradient(145deg, #FFFFFF 0%, #F4F7FB 72%, #E9EEF8 100%)',
        border: `1px solid ${T.cardBorder}`, borderRadius: isPortrait ? 24 : 22,
        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default', overflow: 'hidden', position: 'relative',
      }}
    >
      {innerContent}
    </div>
  );
}

/** Combined thumbnail strip for Playlists matching Image #2 */
function CombinedPlaylistThumb({
  mediaList,
  onSettled,
  onClick,
}: {
  mediaList: SpotLightMedia[];
  onSettled?: () => void;
  onClick?: () => void;
}) {
  const visible = mediaList.slice(0, 2);
  const overflow = mediaList.length > 2 ? mediaList.length - 1 : 0;

  return (
    <div
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: 6,
        background: 'var(--surface, #fff)', border: `1px solid ${T.cardBorder}`,
        borderRadius: 16, boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
        cursor: onClick ? 'pointer' : 'default', boxSizing: 'border-box', flexShrink: 0,
      }}
    >
      {visible.map((media, idx) => {
        const isLastVisible = idx === visible.length - 1;
        const showOverflowBadge = isLastVisible && overflow > 0;
        return (
          <div
            key={media.mediaId || idx}
            style={{
              width: 56, height: 76, borderRadius: 10, overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(180deg, #172033 0%, #101827 100%)', flexShrink: 0,
            }}
          >
            <MediaThumb
              mediaId={media.mediaId}
              mimeType={media.mimeType}
              name={media.name}
              url={media.url}
              orientation={media.orientation}
              onSettled={onSettled}
              width={56}
              height={76}
              bare
            />
            {showOverflowBadge && (
              <div
                style={{
                  position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.72)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 800, color: '#fff', fontFamily: T.font }}>
                  +{overflow}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TypeBadge({ type }: { type: 'Single' | 'Playlist' }) {
  const isPlaylist = type === 'Playlist';
  return (
    <span className={`display-type-badge ${isPlaylist ? 'is-playlist' : ''}`}>
      <Icon name={isPlaylist ? 'playlist_play' : 'image'} size={14} color={isPlaylist ? '#DC2626' : '#475569'} />
      {type}
    </span>
  );
}

function useDisplayMediaPreviewSource(media?: Partial<SpotLightMedia> | null) {
  const mediaId = media?.mediaId;
  const mimeType = media?.mimeType;
  const url = media?.url;
  const directMediaUrl = media?.mediaUrl;
  const src = media?.src;
  const candidateUrls = useMemo(
    () => getDisplayMediaCandidateUrls({ mediaId, mimeType, url, mediaUrl: directMediaUrl, src }),
    [mediaId, mimeType, url, directMediaUrl, src],
  );
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setCurrentUrlIndex(0);
    setFailed(false);
  }, [candidateUrls]);

  const mediaUrl = failed ? null : candidateUrls[currentUrlIndex] ?? null;

  const handleMediaError = () => {
    if (currentUrlIndex < candidateUrls.length - 1) {
      setCurrentUrlIndex((index) => index + 1);
      return;
    }

    setFailed(true);
  };

  return { mediaUrl, failed, handleMediaError };
}

function SecretKeyCard({ secretKey, mobile }: { secretKey?: string | null; mobile?: boolean }) {
  if (!secretKey) return null;

  return (
    <div className={`display-secret-key-card ${mobile ? 'is-mobile' : ''}`}>
      <div className="display-secret-key-left">
        <div className="display-secret-key-icon-box">
          <Icon name="key" size={mobile ? 18 : 20} color={T.accent} />
        </div>
        <div>
          <div className="display-secret-key-label">Secret Key</div>
          <div className="display-secret-key-value">{secretKey}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────
// (Using MediaCarouselPreviewModal for rich single & playlist carousel preview)

function DeleteModal({ open, name, onClose, onConfirm }: { open: boolean; name: string; onClose: () => void; onConfirm: () => void }) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(15,15,20,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 14, padding: '28px 32px', maxWidth: 400, width: '90%', boxShadow: '0 12px 48px rgba(0,0,0,0.18)', fontFamily: T.font }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: T.redSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Icon name="delete" size={24} color={T.red} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.t1, marginBottom: 8 }}>Delete Display?</div>
        <div style={{ fontSize: 13, color: T.t2, lineHeight: 1.6, marginBottom: 28 }}><strong>"{name}"</strong> will be permanently removed.</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 22px', borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: T.red, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function DisplayListPage() {
  const navigate   = useNavigate();
  const isDesktop  = useIsDesktop();
  const isMobile   = !isDesktop;
  const isTabletDesktop = useMediaQuery(TABLET_TABLE_BREAKPOINT);
  const { items, loading, saving, error, fetchDisplays, setEditingItemId, deleteDisplay } = useDisplayStore();
  const { addToast } = useDisplayToastStore();
  const { selectedBranch } = useBranch();
  const setTopBarProps = useDesktopTopBar();
  const showInitialLoading = loading && items.length === 0;
  const currentLocationId = selectedBranch?.id ?? (selectedBranch as any)?.locationId ?? '';
  const lastFetchKeyRef = useRef<string | null>(null);

  useEffect(() => { setTopBarProps({ title: 'Display', breadcrumbs: 'Display' }); }, [setTopBarProps]);

  const [search, setSearch]                   = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [orientationFilter, setOrientationFilter] = useState<'All' | 'Landscape' | 'Portrait'>('All');
  const [deleteTarget, setDeleteTarget] = useState<DisplayRow | null>(null);
  const [previewRow, setPreviewRow]     = useState<DisplayRow | null>(null);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!currentLocationId) {
      lastFetchKeyRef.current = null;
      return;
    }

    const fetchKey = `${currentLocationId}::${debouncedSearch || ''}`;
    if (lastFetchKeyRef.current === fetchKey) return;

    lastFetchKeyRef.current = fetchKey;
    fetchDisplays(currentLocationId, debouncedSearch || undefined);
  }, [currentLocationId, debouncedSearch, fetchDisplays]);

  const displayRows: DisplayRow[] = useMemo(() => {
    const rows: DisplayRow[] = [];

    // Backend already returns displays in the intended order — keep it as-is instead of re-sorting client-side.
    items.forEach((item, index) => {
      const mediaList = [...(item.attribute?.spotLightMedia ?? [])].sort(
        (a, b) => (parseInt(a.sequenceNo, 10) || 0) - (parseInt(b.sequenceNo, 10) || 0),
      );

      const isPlaylist = item.attribute?.mode === 'P' || Boolean(item.attribute?.playlistId) || mediaList.length > 1;
      const type: 'Single' | 'Playlist' = isPlaylist ? 'Playlist' : 'Single';
      const displayName = item.displayName || item.name || mediaList[0]?.name || `Display ${index + 1}`;
      const orientation: 'Landscape' | 'Portrait' = (mediaList[0]?.orientation ?? 'Landscape') === 'Portrait' ? 'Portrait' : 'Landscape';
      const slots = getFormattedScheduleSlots(item);

      rows.push({
        displayItem: item,
        seqNum: index + 1,
        displayName,
        type,
        orientation,
        mediaList,
        slots,
      });
    });

    return rows;
  }, [items]);

  const [pendingImageCount, setPendingImageCount] = useState(0);
  const handleImageSettled = useCallback(() => {
    setPendingImageCount(prev => Math.max(0, prev - 1));
  }, []);

  const filtered = useMemo(() => {
    let list = displayRows;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((row) => row.displayName.toLowerCase().includes(q));
    }
    if (orientationFilter !== 'All') {
      list = list.filter((row) => row.orientation === orientationFilter);
    }
    return list;
  }, [displayRows, search, orientationFilter]);

  const commonSecretKey = useMemo(() => {
    const uniqueKeys = Array.from(new Set(items.map((item) => item.secretKey ?? item.attribute?.pin).filter(Boolean)));
    return uniqueKeys.length === 1 ? uniqueKeys[0] : uniqueKeys[0] ?? '';
  }, [items]);

  const handleEdit = (row: DisplayRow) => {
    setEditingItemId(row.displayItem.id, row.mediaList[0]?.mediaId ?? null);
    navigate(`${displayPaths.edit}?id=${row.displayItem.id}`, { state: { displayItem: row.displayItem } });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    if (!currentLocationId) {
      addToast('No outlet selected — pick a location first.', 'error');
      return;
    }
    const target = deleteTarget;
    setDeleteTarget(null);
    deleteDisplay(target.displayItem.id, currentLocationId)
      .then(() => addToast(`${target.displayName} deleted`, 'info'))
      .catch((err: Error) => addToast(err.message || `Failed to delete ${target.displayName}`, 'error'));
  };

  const handleAddNew = () => { setEditingItemId(null); navigate(displayPaths.create); };
  const retryFetch   = () => {
    if (!currentLocationId) return;
    lastFetchKeyRef.current = null;
    fetchDisplays(currentLocationId, debouncedSearch || undefined);
  };

  const actionBtnStyle: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font };

  // ── MOBILE ────────────────────────────────────────────────────────────────────
  if (showInitialLoading) {
    if (isMobile) {
      return (
        <div style={{ background: T.bg, minHeight: '100vh', fontFamily: T.font, overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
          <MobileShimmer />
          <BottomNav />
        </div>
      );
    }

    return (
      <DesktopLayout>
        <div style={{ width: '100%', minWidth: 0, background: T.bg, boxSizing: 'border-box', fontFamily: T.font }}>
          <DesktopShimmer />
        </div>
      </DesktopLayout>
    );
  }

  if (isMobile) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', fontFamily: T.font, overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ padding: '12px 16px 80px', display: 'flex', flexDirection: 'column', gap: 10, width: '100%', boxSizing: 'border-box' }}>
          <HeroBanner onAdd={handleAddNew} mobile />
          <SecretKeyCard secretKey={commonSecretKey} mobile />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Icon name="search" size={16} color={T.t3} style={{ position: 'absolute', left: 12, top: 10 }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search displays..."
                  style={{ width: '100%', border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '9px 12px 9px 34px', fontSize: 12, fontFamily: T.font, color: T.t1, outline: 'none', background: T.surface, boxSizing: 'border-box' }} />
              </div>
              <button onClick={handleAddNew} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '9px 14px', borderRadius: 12, border: 'none', background: T.accent, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, flexShrink: 0 }}>
                <Icon name="add" size={16} color="#fff" /> Add
              </button>
            </div>
            <div style={{ display: 'flex', background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: 3, gap: 4, alignSelf: 'flex-start' }}>
              {(['All', 'Portrait', 'Landscape'] as const).map((filterOpt) => {
                const active = orientationFilter === filterOpt;
                return (
                  <button
                    key={filterOpt}
                    type="button"
                    onClick={() => setOrientationFilter(filterOpt)}
                    style={{
                      padding: '6px 12px', borderRadius: 7, border: 'none', fontSize: 11, fontWeight: 600,
                      cursor: 'pointer', fontFamily: T.font,
                      background: active ? T.accent : 'transparent',
                      color: active ? '#fff' : T.t2, transition: 'all .15s ease',
                    }}
                  >
                    {filterOpt}
                  </button>
                );
              })}
            </div>
          </div>

          {error ? (
            <div style={{ background: T.card, border: `1px solid #FCA5A5`, borderRadius: 14, padding: '32px 16px', textAlign: 'center' }}>
              <Icon name="wifi_off" size={40} color={T.red} style={{ display: 'block', margin: '0 auto 10px' }} />
              <p style={{ fontSize: 13, color: T.t1, margin: '0 0 12px', fontFamily: T.font }}>{error}</p>
              <button onClick={retryFetch} style={{ padding: '8px 16px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14 }}>
              <DisplayEmptyState search={search} onAdd={handleAddNew} mobile />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map(row => {
                const key = row.displayItem.id;
                const firstMedia = row.mediaList[0];
                return (
                  <div key={key} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: '12px 14px', position: 'relative', boxShadow: T.shadow }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {row.type === 'Playlist' ? (
                        <CombinedPlaylistThumb
                          mediaList={row.mediaList}
                          onSettled={handleImageSettled}
                          onClick={() => setPreviewRow(row)}
                        />
                      ) : (
                        firstMedia && (
                          <MediaThumb
                            mediaId={firstMedia.mediaId}
                            mimeType={firstMedia.mimeType}
                            name={firstMedia.name}
                            url={firstMedia.url}
                            orientation={firstMedia.orientation}
                            onClick={() => setPreviewRow(row)}
                            onSettled={handleImageSettled}
                            width={90}
                            height={65}
                          />
                        )
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <TypeBadge type={row.type} />
                          <span style={{ fontSize: 11, fontWeight: 700, color: T.t3, fontFamily: T.font }}>Seq {row.seqNum}</span>
                        </div>
                        <div title={row.displayName} style={{ fontSize: 13, fontWeight: 700, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: T.font }}>{row.displayName}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                          {row.slots.map((slot, idx) => (
                            <SlotPill key={idx} slot={slot} />
                          ))}
                        </div>
                      </div>
                      <button onClick={() => setActionMenuId(actionMenuId === key ? null : key)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                        <Icon name="more_vert" size={20} color={T.t3} />
                      </button>
                    </div>
                    {actionMenuId === key && (
                      <>
                        <div onClick={() => setActionMenuId(null)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
                        <div style={{ position: 'absolute', top: 44, right: 12, zIndex: 999, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', overflow: 'hidden', minWidth: 140 }}>
                          {[
                            { label: 'Preview', icon: 'play_circle', color: T.t1, action: () => { setActionMenuId(null); setPreviewRow(row); } },
                            { label: 'Edit',    icon: 'edit',        color: T.t1, action: () => { setActionMenuId(null); handleEdit(row); } },
                            { label: 'Delete',  icon: 'delete',      color: T.red, action: () => { setActionMenuId(null); setDeleteTarget(row); } },
                          ].map(item => (
                            <button key={item.label} onClick={item.action} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '11px 16px', fontSize: 13, color: item.color, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>
                              <Icon name={item.icon} size={16} color={item.color} /> {item.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <BottomNav />
        <MediaCarouselPreviewModal
          open={!!previewRow}
          title={previewRow?.displayName}
          items={previewRow?.mediaList ?? []}
          onClose={() => setPreviewRow(null)}
        />
        <DeleteModal open={!!deleteTarget} name={deleteTarget?.displayName ?? ''} onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteConfirm} />
        <DisplayToastContainer />
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────────
  return (
    <DesktopLayout>
      <div style={{ width: '100%', minWidth: 0, background: T.bg, boxSizing: 'border-box', fontFamily: T.font }}>
        <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>

          <HeroBanner onAdd={handleAddNew} />
          <SecretKeyCard secretKey={commonSecretKey} />

          {/* Search and Orientation Filter */}
          <div className="display-filter-toolbar">
            <div className="display-search-box">
              <Icon name="search" size={18} color={T.t3} style={{ position: 'absolute', left: 16, top: 12 }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by display name"
                className="display-search-input" />
            </div>

            <div className="display-filter-segmented">
              {(['All', 'Portrait', 'Landscape'] as const).map((filterOpt) => {
                const active = orientationFilter === filterOpt;
                return (
                  <button
                    key={filterOpt}
                    type="button"
                    onClick={() => setOrientationFilter(filterOpt)}
                    className={`display-filter-btn ${active ? 'is-active' : ''}`}
                  >
                    {filterOpt}
                  </button>
                );
              })}
            </div>
          </div>

          {error ? (
            <div style={{ background: T.card, border: `1px solid #FCA5A5`, borderRadius: 14, padding: '48px 32px', textAlign: 'center' }}>
              <Icon name="wifi_off" size={48} color={T.red} style={{ display: 'block', margin: '0 auto 12px' }} />
              <p style={{ fontSize: 15, color: T.t1, margin: '0 0 16px', fontFamily: T.font }}>{error}</p>
              <button onClick={retryFetch} style={{ padding: '9px 18px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}>Retry</button>
            </div>
          ) : (
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, boxShadow: T.cardShadow, overflowX: isTabletDesktop ? 'auto' : 'hidden', overflowY: 'hidden' }}>

              {/* Column Headers */}
              <div style={{ display: 'grid', gridTemplateColumns: GRID, width: isTabletDesktop ? TABLET_TABLE_MIN_WIDTH : '100%', minWidth: isTabletDesktop ? TABLET_TABLE_MIN_WIDTH : undefined, gap: 8, padding: '12px 14px', background: T.bg, borderLeft: '3px solid transparent', overflow: 'hidden', boxSizing: 'border-box' }}>
                {HEADERS.map(h => (
                  <div
                    key={h.key}
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: T.t3,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontFamily: T.font,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: h.key === 'actions' ? 'center' : 'flex-start',
                    }}
                  >
                    {h.label}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {filtered.length === 0 ? (
                <DisplayEmptyState search={search} onAdd={handleAddNew} />
              ) : (
                <div style={{ position: 'relative' }}>
                  <div>
                    {filtered.map((row, i) => {
                      const key = row.displayItem.id;
                      const firstMedia = row.mediaList[0];
                      return (
                        <div
                          key={key}
                          style={{
                            display: 'grid', gridTemplateColumns: GRID, width: isTabletDesktop ? TABLET_TABLE_MIN_WIDTH : '100%', minWidth: isTabletDesktop ? TABLET_TABLE_MIN_WIDTH : undefined, gap: 8,
                            padding: '14px', alignItems: 'center',
                            borderBottom: i < filtered.length - 1 ? `1px solid ${T.divider}` : 'none',
                            background: 'transparent', borderLeft: '3px solid transparent',
                            cursor: saving ? 'not-allowed' : 'default', transition: 'background .1s', overflow: 'hidden', boxSizing: 'border-box',
                            opacity: saving ? 0.7 : 1,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = T.hover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {/* SEQ */}
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.t2, fontFamily: T.font }}>{row.seqNum}</div>

                          {/* TYPE */}
                          <div>
                            <TypeBadge type={row.type} />
                          </div>

                          {/* MEDIA */}
                          <div>
                            {row.type === 'Playlist' ? (
                              <CombinedPlaylistThumb
                                mediaList={row.mediaList}
                                onSettled={handleImageSettled}
                                onClick={() => setPreviewRow(row)}
                              />
                            ) : (
                              firstMedia && (
                                <MediaThumb
                                  mediaId={firstMedia.mediaId}
                                  mimeType={firstMedia.mimeType}
                                  name={firstMedia.name}
                                  url={firstMedia.url}
                                  orientation={firstMedia.orientation}
                                  onClick={() => setPreviewRow(row)}
                                  onSettled={handleImageSettled}
                                  width={110}
                                  height={74}
                                />
                              )
                            )}
                          </div>

                          {/* DISPLAY NAME */}
                          <div title={row.displayName} style={{ fontSize: 14, fontWeight: 700, color: T.t1, fontFamily: T.font, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.displayName}
                          </div>

                          {/* ORIENTATION */}
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '4px 10px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 700,
                                fontFamily: T.font,
                                background: row.orientation === 'Portrait' ? '#EEF2FF' : '#ECFDF5',
                                color: row.orientation === 'Portrait' ? '#4F46E5' : '#059669',
                                border: `1px solid ${row.orientation === 'Portrait' ? '#C7D2FE' : '#A7F3D0'}`,
                              }}
                            >
                              <OrientationIcon
                                orientation={row.orientation}
                                size={14}
                                color={row.orientation === 'Portrait' ? '#4F46E5' : '#059669'}
                              />
                              {row.orientation}
                            </span>
                          </div>

                          {/* SLOTS */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                            {row.slots.map((slot, idx) => (
                              <SlotPill key={idx} slot={slot} />
                            ))}
                          </div>

                          {/* ACTIONS */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); handleEdit(row); }}
                              title="Edit display"
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: 6,
                                borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background .15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(210,58,59,0.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                            >
                              <Icon name="edit" size={18} color={T.accent} />
                            </button>
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); setDeleteTarget(row); }}
                              title="Delete display"
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: 6,
                                borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'background .15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.08)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                            >
                              <Icon name="delete" size={18} color={T.red} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
        <MediaCarouselPreviewModal
          open={!!previewRow}
          title={previewRow?.displayName}
          items={previewRow?.mediaList ?? []}
          onClose={() => setPreviewRow(null)}
        />
        <DeleteModal open={!!deleteTarget} name={deleteTarget?.displayName ?? ''} onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteConfirm} />
        <DisplayToastContainer />
    </DesktopLayout>
  );
}
